import { filterManagedUsers } from '../utils/manager-dashboard-filters';
import { ISelectOption } from '@/components/Fields';
import { ISortConfig } from '@/components/Tables';
import { TABLE_LABELS } from '@/constants';
import { TManagerDashboardFilter } from '../types';
import { useMemo } from 'react';
import {
  IManagedUserRecentSummary,
  IManagerProduct,
  IManagerTeamSummary,
} from '@/types';

/** Props for the useManagerDashboardMemoData hook */
interface UseManagerDashboardMemoDataProps {
  /** The currently active filter applied to the dashboard */
  activeFilter: TManagerDashboardFilter;
  /** The current list of filtered users displayed in the table */
  filteredUsers: IManagedUserRecentSummary[];
  /** The currently active certification product */
  managerActiveProduct: IManagerProduct | null;
  /** The manager's team summary data from the global store */
  managersTeamSummary: IManagerTeamSummary | null;
  /** The current sort configuration for the table */
  sortConfig: ISortConfig;
}

/**
 * Hook to encapsulate the memoized derived data for the Manager Dashboard.
 * Computes the managed users for the active product and the filtered subsets
 * used by the summary cards and filter logic.
 */
export const useManagerDashboardMemoData = ({
  activeFilter,
  filteredUsers,
  managerActiveProduct,
  managersTeamSummary,
  sortConfig,
}: UseManagerDashboardMemoDataProps) => {
  const managedUsers: IManagedUserRecentSummary[] = useMemo(() => {
    const activeCertification = managersTeamSummary?.certifications?.find((c) => {
      return c.certificationId === managerActiveProduct?.certificationId;
    });

    return activeCertification?.managedUsers || [];
  }, [managersTeamSummary, managerActiveProduct]);

  const usersCertified = useMemo(
    () => filterManagedUsers(managedUsers, 'certified'),
    [managedUsers],
  );

  const usersPassedPractice = useMemo(
    () => filterManagedUsers(managedUsers, 'practicePassed'),
    [managedUsers],
  );

  const usersNeedingAttention = useMemo(
    () => filterManagedUsers(managedUsers, 'needsAttention'),
    [managedUsers],
  );

  const showFilterResultsText = useMemo(() => {
    const activeFilterExists = activeFilter !== null;
    const sortApplied = sortConfig?.direction !== null;
    return activeFilterExists || sortApplied;
  }, [activeFilter, sortConfig]);

  const filteredResultsText = useMemo(() => {
    if (!activeFilter) return '';
    return `${TABLE_LABELS.SHOWING_FILTER_RESULTS} (${filteredUsers.length}/${managedUsers.length})`;
  }, [activeFilter, filteredUsers.length, managedUsers.length]);

  const productOptions: ISelectOption[] = useMemo(() => {
    if (!managersTeamSummary) return [];
    return managersTeamSummary?.certifications?.map((cert) => ({
      label: cert.certificationTitle,
      value: cert.certificationId.toString(),
    }));
  }, [managersTeamSummary]);

  return {
    filteredResultsText,
    managedUsers,
    productOptions,
    showFilterResultsText,
    usersCertified,
    usersNeedingAttention,
    usersPassedPractice,
  };
};
